/**
 * Argus Live — frontend SSE consumer for `refreshMode='subscribe'` bindings
 * (Sprint 11 / FIX2-FRONTEND defect 1).
 *
 * ## Why this exists
 *
 * The Sprint 11 backend (`packages/api/src/routes/v1/argus-live/subscribe.ts`)
 * exposes a fully-feature-complete SSE channel at
 * `GET /api/v1/argus-live/dashboards/:id/subscribe?tables=<slugs>` plus a
 * long-poll fallback at `GET …/poll`. Until this hook, NOTHING on the
 * frontend opened that channel — `refreshMode='subscribe'` bindings were a
 * no-op and iframes never received delta events.
 *
 * ## What this hook does
 *
 * Given a dashboard ID + the dashboard's binding manifest + a dispatcher
 * (from `useArgusLiveDashboardHost`), this hook:
 *
 *   1. Scans the manifest for any `refreshMode='subscribe'` bindings and
 *      unions their `tables[]` allowlists into one slug set.
 *   2. Opens `…/subscribe?tables=<slugs>` via `apiClient.requestStream`
 *      (which threads Bearer auth + 401 refresh under the hood).
 *   3. Parses the `text/event-stream` body line-by-line. Recognised event
 *      types per the backend payload shape:
 *        - `connected`           — channel ready; carries subscriptionId + asOf.
 *        - `heartbeat`           — 30s keep-alive; advances asOf watermark.
 *        - `delta`               — `{ tableSlug, rowIds }`. Per spec
 *                                  §7.8 + §11 we re-fetch each affected
 *                                  binding and push the new data into the
 *                                  iframe as a `DATA_RESPONSE`.
 *        - `truncated`           — server hit LIMIT; more deltas imminent.
 *                                  We surface this on the hook's state so
 *                                  callers can show a "catching up" badge.
 *        - `reconnect_required`  — 30min hard cap reached. Close cleanly
 *                                  and reopen with `?since=<asOf>`.
 *        - `error`               — server-side poll cycle failed. Treated
 *                                  as a transient hiccup; channel itself
 *                                  stays open per spec.
 *   4. On stream-level error or close-without-reconnect, falls back to
 *      `…/poll` with `since=<asOf>` and `timeout_ms=30000`, retrying.
 *
 * The hook never holds row payloads itself — per v1.1 amendment row 11 /
 * Q12 the server pushes row IDs ONLY, and the host re-fetches the data
 * through the existing classification-gated dispatcher.
 *
 * ## What "re-fetch the rows" means in v1
 *
 * The original defect text references a `GET /api/v1/tables/:slug/rows/:rowKey`
 * endpoint — that route is not in v1; per spec §7.8 the row-id payload is
 * intentionally opaque and the host re-runs the binding's `fetchData` call,
 * which is what actually puts fresh data into the iframe. We map every
 * `tableSlug` in a delta back to the binding(s) whose `tables[]` includes
 * that slug and call `dispatcher.fetchData({ dashboardId, bindingId })`
 * for each. The fresh `data` is then forwarded to the iframe as a
 * `DATA_RESPONSE` with a fresh `requestId` so the iframe runtime treats
 * it as a host-initiated update.
 *
 * ## Failure modes (fail-soft)
 *
 * - No subscribe bindings → hook does nothing (returns idle state).
 * - 4xx on SSE open       → caller sees `error` in state; hook does NOT
 *                            retry (a 403 means the user lost their grant,
 *                            a 400 means the binding scope is wrong — both
 *                            are non-transient and need an editor action).
 * - 5xx / stream close    → falls back to long-poll. If the long-poll
 *                            also fails, we wait 5s and retry. Capped at
 *                            10 attempts before surfacing a sticky error.
 *
 * ## Why not native `EventSource`
 *
 * Browsers' `EventSource` does NOT support Authorization headers — it can
 * only authenticate via cookies. The Argus Live API is Bearer-only for the
 * SPA (cookies are NOT issued for `/api/v1/*`). Using `fetch` with the
 * existing `apiClient.requestStream` gives us:
 *   - Authorization: Bearer <jwt>
 *   - 401 refresh-then-retry (same loop as JSON endpoints)
 *   - AbortSignal cancellation
 * at the cost of a hand-rolled SSE parser, which is ~30 lines and
 * regression-tested below.
 */

import { useEffect, useRef, useState } from 'react';
import { uuidv4 } from './uuid';
import { apiClient } from './api-client';
import type { ArgusLiveHostDispatcher } from '../components/argus-live/post-message-host-core';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Public types ────────────────────────────────────────────────────────────

/** Minimal binding shape the hook needs. A subset of DashboardBinding. */
export interface SubscribeBindingSummary {
  id: string;
  refreshMode: string;
  tables?: string[] | null;
  params?: Record<string, unknown> | null;
}

export type SubscribeStatus =
  | 'idle' // no subscribe bindings → not connected
  | 'connecting' // SSE handshake in flight
  | 'live' // SSE channel open, deltas flowing
  | 'reconnecting' // received reconnect_required OR mid-fallback
  | 'polling' // SSE failed; long-poll fallback active
  | 'error'; // sticky failure surfaced to caller

export interface UseArgusLiveSubscribeOptions {
  /** When false, the hook is a no-op (used for the editor preview's
   *  "static data" toggle). Defaults to true. */
  enabled?: boolean;
  /** Optional version pin so live deltas refetch the same snapshot the iframe renders. */
  versionId?: string | null;
  /** Override the fetch implementation for tests. */
  fetchImpl?: typeof fetch;
}

export interface UseArgusLiveSubscribeReturn {
  status: SubscribeStatus;
  /** Server-side asOf of the most recent event we received. */
  asOf: string | null;
  /** Slugs the channel is currently watching (server may collapse dupes). */
  watching: string[];
  /** Total number of delta events processed (for dev-tools / debug pane). */
  deltaCount: number;
  /** Total number of times we pushed a DATA_RESPONSE to the iframe. */
  refreshCount: number;
  /** Last error message, if any. Cleared when status returns to 'live'. */
  error: string | null;
  /** True iff server hinted `truncated` since the last `delta`. */
  catchingUp: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const FALLBACK_RETRY_DELAY_MS = 5_000;
const FALLBACK_MAX_ATTEMPTS = 10;
const LONG_POLL_TIMEOUT_MS = 30_000;

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Wires the Sprint 11 SSE/long-poll backend to the iframe host.
 *
 * @param dashboardId      Dashboard whose subscribe-mode bindings we manage.
 * @param bindings         Manifest read off the version. Only bindings with
 *                         `refreshMode === 'subscribe'` matter; the rest are
 *                         silently ignored.
 * @param dispatcher       Host dispatcher (from `useArgusLiveDashboardHost`).
 *                         We call `dispatcher.fetchData(...)` to re-fetch
 *                         each affected binding and `dispatcher.sendToIframe`
 *                         to push the result.
 * @param options.enabled  Set to false in non-viewer surfaces.
 */
export function useArgusLiveSubscribe(
  dashboardId: string,
  bindings: SubscribeBindingSummary[],
  dispatcher: ArgusLiveHostDispatcher | null,
  options: UseArgusLiveSubscribeOptions = {},
): UseArgusLiveSubscribeReturn {
  const enabled = options.enabled !== false;
  const fetchImpl = options.fetchImpl;
  const versionId = options.versionId ?? null;

  // ─── Compute the slug set and the slug→bindings map ──────────────
  //
  // We re-derive both on every render but a small `key` string lets the
  // effect avoid re-opening the SSE channel when the manifest object
  // identity changed but the actual contents didn't.
  const { slugs, slugToBindings, key } = computeScope(bindings);

  const [status, setStatus] = useState<SubscribeStatus>('idle');
  const [asOf, setAsOf] = useState<string | null>(null);
  const [watching, setWatching] = useState<string[]>([]);
  const [deltaCount, setDeltaCount] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [catchingUp, setCatchingUp] = useState(false);

  // Resume cursor is intentionally narrower than the display `asOf`.
  // Heartbeats use wall-clock timestamps, while this cursor only advances
  // when the backend confirms a data watermark.
  const resumeSinceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !dispatcher || slugs.length === 0 || !dashboardId) {
      setStatus('idle');
      setWatching([]);
      return;
    }

    const lifecycleController = new AbortController();
    let activeStreamController: AbortController | null = null;
    let cancelled = false;
    let fallbackAttempts = 0;
    let reconnectRequested = false;

    const sendRowsToIframe = async (tableSlug: string) => {
      // Re-fetch every binding that watches this slug. We push the
      // fresh data into the iframe regardless of which row IDs changed
      // — the iframe owns the rendering decision and the binding's
      // tool may return the full table or just the affected slice.
      const affected = slugToBindings.get(tableSlug) ?? [];
      for (const b of affected) {
        try {
          const res = await dispatcher.fetchData({
            dashboardId,
            versionId: versionId ?? undefined,
            bindingId: b.id,
            requestId: uuidv4(),
            params: b.params ?? {},
          });
          if (cancelled) return;
          dispatcher.sendToIframe({
            type: 'DATA_RESPONSE',
            requestId: uuidv4(),
            bindingId: b.id,
            data: res.data,
            classification: res.classification as {
              sensitivity: Sensitivity;
              exportControl: ExportControl;
            },
            dataAsOf: res.dataAsOf,
          });
          setRefreshCount((n) => n + 1);
        } catch (err) {
          // A single binding refetch failing is non-fatal — log and move
          // on. The next delta will re-attempt.
          // eslint-disable-next-line no-console
          console.warn(
            '[argus-live-subscribe] re-fetch failed for binding',
            b.id,
            (err as Error).message,
          );
        }
      }
    };

    const handleEvent = async (ev: Record<string, unknown>) => {
      const type = typeof ev.type === 'string' ? ev.type : '';
      const evAsOf =
        typeof ev.asOf === 'string' && ev.asOf.length > 0 ? ev.asOf : null;
      switch (type) {
        case 'connected': {
          if (evAsOf) setAsOf(evAsOf);
          if (Array.isArray(ev.watching)) {
            setWatching(
              ev.watching.filter((s): s is string => typeof s === 'string'),
            );
          }
          setStatus('live');
          setError(null);
          setCatchingUp(false);
          break;
        }
        case 'heartbeat': {
          if (evAsOf) setAsOf(evAsOf);
          break;
        }
        case 'delta': {
          if (evAsOf) {
            setAsOf(evAsOf);
            resumeSinceRef.current = evAsOf;
          }
          setDeltaCount((n) => n + 1);
          setCatchingUp(false);
          const tableSlug = typeof ev.tableSlug === 'string' ? ev.tableSlug : '';
          if (tableSlug) await sendRowsToIframe(tableSlug);
          break;
        }
        case 'truncated': {
          setCatchingUp(true);
          break;
        }
        case 'reconnect_required': {
          setStatus('reconnecting');
          reconnectRequested = true;
          activeStreamController?.abort();
          break;
        }
        case 'error': {
          if (evAsOf) setAsOf(evAsOf);
          // Server-side hiccup. Don't surface as sticky; the channel
          // stays open.
          setError(
            typeof ev.message === 'string'
              ? ev.message
              : 'Server-side poll cycle failed; retrying.',
          );
          break;
        }
        default:
          // Unknown event type — ignore, defense in depth against a
          // future server protocol bump.
          break;
      }
    };

    const openSse = async () => {
      setStatus('connecting');
      setError(null);
      const attemptController = new AbortController();
      activeStreamController = attemptController;
      const abortAttempt = () => attemptController.abort();
      lifecycleController.signal.addEventListener('abort', abortAttempt, {
        once: true,
      });
      try {
        const params = new URLSearchParams({ tables: slugs.join(',') });
        const since = resumeSinceRef.current;
        if (since) params.set('since', since);
        if (versionId) params.set('versionId', versionId);
        const url = `/argus-live/dashboards/${dashboardId}/subscribe?${params.toString()}`;
        const res = await openStream(url, attemptController.signal, fetchImpl);
        if (!res.ok) {
          throw new Error(`SSE open failed: HTTP ${res.status}`);
        }
        if (!res.body) {
          throw new Error('SSE open failed: empty body');
        }
        fallbackAttempts = 0;
        await consumeSse(res.body, handleEvent, attemptController.signal);
        if (reconnectRequested) {
          reconnectRequested = false;
          if (!cancelled && !lifecycleController.signal.aborted) {
            await openSse();
          }
          return;
        }
        // Stream ended cleanly without a `reconnect_required`. Treat as
        // a soft disconnect and reopen unless we're being torn down.
        if (!cancelled && lifecycleController.signal.aborted === false) {
          // Re-enter via fallback path so we don't tight-loop on a
          // misbehaving proxy.
          throw new Error('stream_closed_without_reconnect');
        }
      } catch (err) {
        if (cancelled || lifecycleController.signal.aborted) return;
        if (reconnectRequested) {
          reconnectRequested = false;
          await openSse();
          return;
        }
        // SSE failed — fall through to long-poll.
        await runLongPollFallback(err as Error);
      } finally {
        lifecycleController.signal.removeEventListener('abort', abortAttempt);
        if (activeStreamController === attemptController) {
          activeStreamController = null;
        }
      }
    };

    const runLongPollFallback = async (initialErr: Error) => {
      while (!cancelled && fallbackAttempts < FALLBACK_MAX_ATTEMPTS) {
        fallbackAttempts += 1;
        setStatus('polling');
        setError(
          fallbackAttempts === 1
            ? `Live channel unavailable (${initialErr.message}); using long-poll fallback`
            : `Long-poll attempt ${fallbackAttempts}`,
        );
        try {
          const since =
            resumeSinceRef.current ??
            new Date(Date.now() - 60_000).toISOString();
          const params = new URLSearchParams({
            tables: slugs.join(','),
            since,
            timeout_ms: String(LONG_POLL_TIMEOUT_MS),
          });
          if (versionId) params.set('versionId', versionId);
          const url = `/argus-live/dashboards/${dashboardId}/poll?${params.toString()}`;
          const body = await pollOnce(url, lifecycleController.signal, fetchImpl);
          if (!body) {
            await delay(FALLBACK_RETRY_DELAY_MS, lifecycleController.signal);
            continue;
          }
          // Apply each delta the same way as SSE.
          const deltas = Array.isArray(body.deltas) ? body.deltas : [];
          for (const d of deltas) {
            await handleEvent(d as Record<string, unknown>);
          }
          if (typeof body.asOf === 'string') {
            setAsOf(body.asOf);
            resumeSinceRef.current = body.asOf;
          }
          if (Array.isArray(body.watching)) {
            setWatching(
              body.watching.filter((s: unknown): s is string =>
                typeof s === 'string',
              ),
            );
          }
          fallbackAttempts = 0;
          // After a successful poll cycle, attempt SSE upgrade again on
          // the next iteration — call openSse via a recursive entry.
          if (cancelled) return;
          setStatus('reconnecting');
          await openSse();
          return;
        } catch {
          if (cancelled || lifecycleController.signal.aborted) return;
          // Loop and retry after a short delay.
          await delay(FALLBACK_RETRY_DELAY_MS, lifecycleController.signal);
          continue;
        }
      }
      if (!cancelled) {
        setStatus('error');
        setError('Live channel and long-poll fallback both unavailable.');
      }
    };

    void openSse();
    return () => {
      cancelled = true;
      lifecycleController.abort();
    };
    // `key` captures the relevant slug-set identity; dispatcher + dashboardId
    // are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, key, dispatcher, enabled, versionId]);

  return {
    status,
    asOf,
    watching,
    deltaCount,
    refreshCount,
    error,
    catchingUp,
  };
}

// ─── Internals (exported for testability) ───────────────────────────────────

export function computeScope(bindings: SubscribeBindingSummary[]): {
  slugs: string[];
  slugToBindings: Map<string, SubscribeBindingSummary[]>;
  key: string;
} {
  const slugToBindings = new Map<string, SubscribeBindingSummary[]>();
  const slugSet = new Set<string>();
  for (const b of bindings ?? []) {
    if (!b || b.refreshMode !== 'subscribe') continue;
    const tables = Array.isArray(b.tables) ? b.tables : [];
    for (const slug of tables) {
      if (typeof slug !== 'string' || slug.length === 0) continue;
      slugSet.add(slug);
      const arr = slugToBindings.get(slug);
      if (arr) arr.push(b);
      else slugToBindings.set(slug, [b]);
    }
  }
  const slugs = [...slugSet].sort();
  const bindingKey = bindings
    .filter((b) => b && b.refreshMode === 'subscribe')
    .map((b) =>
      [
        b.id,
        Array.isArray(b.tables) ? [...b.tables].sort().join(',') : '',
        stableJsonForSubscribeKey(b.params ?? {}),
      ].join(':'),
    )
    .sort()
    .join('|');
  return {
    slugs,
    slugToBindings,
    key: `${slugs.join('|')}::${bindingKey}`,
  };
}

function stableJsonForSubscribeKey(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJsonForSubscribeKey(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([key, item]) =>
          `${JSON.stringify(key)}:${stableJsonForSubscribeKey(item)}`,
      )
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * Opens an SSE-style fetch stream. Uses `apiClient.requestStream` in
 * production so Bearer auth + 401 refresh are handled exactly like every
 * other authenticated API call; tests inject `fetchImpl`.
 */
async function openStream(
  endpoint: string,
  signal: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<Response> {
  if (fetchImpl) {
    return fetchImpl(endpoint, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      signal,
    });
  }
  return apiClient.requestStream(endpoint, { method: 'GET', signal });
}

/**
 * Reads a `text/event-stream` body and yields one JSON-parsed event per
 * SSE message. Handles split events across chunks. The backend emits
 * `data: <json>\n\n` only (no named events / id / retry); a defensive
 * `event:` line is tolerated and stripped.
 */
export async function consumeSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: Record<string, unknown>) => Promise<void> | void,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE messages are separated by a blank line.
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const payload = parseSseEvent(raw);
        if (payload) {
          // Sequential so re-fetches don't race when many deltas land.
          await onEvent(payload);
        }
        idx = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Parses one SSE message (lines up to a blank-line terminator). Joins
 * multiple `data:` lines per the spec, ignores `event:`, `id:`, `retry:`.
 */
export function parseSseEvent(raw: string): Record<string, unknown> | null {
  const lines = raw.split('\n');
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    // event:/id:/retry: deliberately ignored.
  }
  if (dataLines.length === 0) return null;
  const text = dataLines.join('\n');
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

async function pollOnce(
  endpoint: string,
  signal: AbortSignal,
  fetchImpl?: typeof fetch,
): Promise<{
  deltas: unknown[];
  asOf?: string;
  watching?: unknown[];
} | null> {
  try {
    if (fetchImpl) {
      const res = await fetchImpl(endpoint, { method: 'GET', signal });
      if (!res.ok) return null;
      return await res.json();
    }
    // apiClient.get auto-prepends /api/v1; we already include the api
    // prefix shape (`/argus-live/...`) so this matches.
    const json = await apiClient.get<{
      deltas: unknown[];
      asOf?: string;
      watching?: unknown[];
    }>(endpoint, { signal });
    return json;
  } catch {
    return null;
  }
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(t);
      resolve();
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
